import api from './api';

export type FlujoFondosVista = 'semanal' | 'mensual';

export interface FlujoFondosConfig {
  incluirCxc: boolean;
  incluirCxp: boolean;
  incluirSaldos: boolean;
  incluirChequesTerceros: boolean;
  incluirChequesPropios: boolean;
  incluirRemitos: boolean;
  incluirPedidos: boolean;
  incluirOrdenesCompra: boolean;
  incluirRecepciones: boolean;
  incluirPresupuestados: boolean;
  offsetDiasRemitos: number;
  offsetDiasPedidos: number;
  offsetDiasOrdenesCompra: number;
  offsetDiasRecepciones: number;
  vistaDefault: string;
  periodosDefault: number;
  umbralExcedente: number;
  esOverrideUsuario?: boolean;
}

export interface FlujoFondosFuenteResumen {
  fuente: string;
  ingresos: number;
  egresos: number;
  cantidad: number;
}

export interface FlujoFondosPeriodo {
  indice: number;
  desde: string;
  hasta: string;
  etiqueta: string;
  esVencido: boolean;
  ingresos: number;
  egresos: number;
  neto: number;
  saldoAcumulado: number;
  alertaNegativo: boolean;
  alertaExcedente: boolean;
  porFuente: FlujoFondosFuenteResumen[];
}

export interface FlujoFondosKpis {
  saldoInicial: number;
  totalIngresos: number;
  totalEgresos: number;
  saldoFinal: number;
  peorSaldo: number;
  peorSaldoFecha?: string | null;
  periodosNegativos: number;
  picoExcedente: number;
  picoExcedenteFecha?: string | null;
  umbralExcedente: number;
}

export interface FlujoFondosProyeccion {
  vista: FlujoFondosVista;
  periodos: number;
  fechaDesde: string;
  fechaHasta: string;
  kpis: FlujoFondosKpis;
  periodosDetalle: FlujoFondosPeriodo[];
  configEfectiva: FlujoFondosConfig;
}

export interface FlujoFondosLinea {
  fuente: string;
  certeza: string;
  sentido: string;
  fechaProyectada: string;
  importe: number;
  comprobante?: string | null;
  tercero?: string | null;
  nroTercero?: number | null;
  nroFormulario?: number | null;
  referencia?: string | null;
  notas?: string | null;
}

export interface FlujoFondosDetalle {
  desde: string;
  hasta: string;
  fuente?: string | null;
  resumenPorFuente: FlujoFondosFuenteResumen[];
  items: FlujoFondosLinea[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface FlujoFondosPresupuestado {
  id: number;
  tipo: string;
  fecha: string;
  importe: number;
  concepto: string;
  notas?: string | null;
  activo: boolean;
}

export interface FlujoFondosPresupuestadoInput {
  tipo: string;
  fecha: string;
  importe: number;
  concepto: string;
  notas?: string | null;
  activo?: boolean;
}

export interface FlujoFondosProyeccionParams {
  vista?: FlujoFondosVista;
  periodos?: number;
  fechaDesde?: string;
  incluirCxc?: boolean;
  incluirCxp?: boolean;
  incluirSaldos?: boolean;
  incluirChequesTerceros?: boolean;
  incluirChequesPropios?: boolean;
  incluirRemitos?: boolean;
  incluirPedidos?: boolean;
  incluirOrdenesCompra?: boolean;
  incluirRecepciones?: boolean;
  incluirPresupuestados?: boolean;
}

export const FUENTE_LABELS: Record<string, string> = {
  cxc: 'Cuentas por cobrar',
  cxp: 'Cuentas por pagar',
  saldos: 'Saldos líquidos',
  chequesTerceros: 'Cheques de terceros',
  chequesPropios: 'Cheques propios',
  remitos: 'Remitos no facturados',
  pedidos: 'Pedidos',
  ordenesCompra: 'Órdenes de compra',
  recepciones: 'Recepciones no facturadas',
  presupuestados: 'Presupuestados',
};

const BASE = '/api/TesoreriaFlujoFondos';

const tesoreriaFlujoFondosService = {
  async getConfig(): Promise<FlujoFondosConfig> {
    const { data } = await api.get(`${BASE}/config`);
    return data;
  },

  async saveConfig(config: FlujoFondosConfig, paraUsuario = false): Promise<FlujoFondosConfig> {
    const { data } = await api.put(`${BASE}/config`, config, { params: { paraUsuario } });
    return data;
  },

  async getProyeccion(params: FlujoFondosProyeccionParams): Promise<FlujoFondosProyeccion> {
    const { data } = await api.get(`${BASE}/proyeccion`, { params });
    return data;
  },

  async getDetalle(params: {
    desde: string;
    hasta: string;
    fuente?: string;
    pageNumber?: number;
    pageSize?: number;
    incluirVencidosAntes?: boolean;
  }): Promise<FlujoFondosDetalle> {
    const { data } = await api.get(`${BASE}/detalle`, { params });
    return data;
  },

  async listPresupuestados(params?: {
    desde?: string;
    hasta?: string;
    soloActivos?: boolean;
  }): Promise<FlujoFondosPresupuestado[]> {
    const { data } = await api.get(`${BASE}/presupuestados`, { params });
    return data;
  },

  async createPresupuestado(input: FlujoFondosPresupuestadoInput): Promise<FlujoFondosPresupuestado> {
    const { data } = await api.post(`${BASE}/presupuestados`, input);
    return data;
  },

  async updatePresupuestado(id: number, input: FlujoFondosPresupuestadoInput): Promise<FlujoFondosPresupuestado> {
    const { data } = await api.put(`${BASE}/presupuestados/${id}`, input);
    return data;
  },

  async deletePresupuestado(id: number): Promise<void> {
    await api.delete(`${BASE}/presupuestados/${id}`);
  },
};

export default tesoreriaFlujoFondosService;
